import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "./AdBanner.scss";

const FALLBACK_API_BASE =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_API ||
  (typeof window !== "undefined" && window.location.hostname.includes("onrender.com")
    ? "https://cvanto-api.onrender.com"
    : "http://localhost:5000");

async function postJson(url, body) {
  try {
    await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body || {}),
      keepalive: true,
    });
  } catch (e) {
    console.warn("[ads] post fail:", url, e);
  }
}

function safeStr(v) {
  return String(v ?? "");
}

function getHost(url) {
  try {
    const u = new URL(String(url || ""));
    return u.hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

// Mirrors server/routes/ads.js's isSafeInternalPath and AdsCabinet.js's
// client-side copy of the same check — defense in depth so a malformed or
// tampered /serve response can never be used to navigate somewhere unsafe.
// Exported for unit tests.
export function isSafeInternalPath(value) {
  const v = String(value || "").trim();
  if (!v) return false;
  if (!v.startsWith("/")) return false;
  if (v.startsWith("//")) return false;
  if (v.includes("://")) return false;
  if (v.includes("\\")) return false;
  return true;
}

// Pure — turns a campaign into what a click should do, without doing it.
// { type: "internal", value } -> navigate(value) via the app router, no
//   full page reload.
// { type: "external", value } -> window.open(value, "_blank", "noopener,noreferrer").
// { type: "none" } -> nothing (no destination, or an unsafe/invalid one).
// destinationType/destinationValue (Phase A) win; a legacy-only targetUrl
// (pre-Phase-A campaigns) is still honored as external — same backward
// compatibility server/routes/ads.js keeps.
export function resolveClickAction(campaign) {
  if (!campaign) return { type: "none" };

  if (campaign.destinationType === "internal") {
    return isSafeInternalPath(campaign.destinationValue)
      ? { type: "internal", value: campaign.destinationValue }
      : { type: "none" };
  }

  const externalUrl =
    campaign.destinationType === "external"
      ? campaign.destinationValue || campaign.targetUrl
      : campaign.targetUrl;

  return externalUrl ? { type: "external", value: externalUrl } : { type: "none" };
}

export default function AdBanner({ campaign, apiBase }) {
  const API_BASE = apiBase || FALLBACK_API_BASE;
  const navigate = useNavigate();

  const action = useMemo(() => resolveClickAction(campaign), [campaign]);
  const host = useMemo(
    () => getHost(action.type === "external" ? action.value : ""),
    [action]
  );

  const onClick = useCallback(() => {
    if (!campaign || !campaign.id) return;

    postJson(`${API_BASE}/api/ads/click`, { campaignId: campaign.id });

    if (action.type === "internal") {
      // in-app navigation, no full page reload
      navigate(action.value);
    } else if (action.type === "external") {
      window.open(action.value, "_blank", "noopener,noreferrer");
    }
  }, [campaign, API_BASE, action, navigate]);

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick();
      }
    },
    [onClick]
  );

  if (!campaign) return null;

  const title = safeStr(campaign.title).trim();
  const desc = safeStr(campaign.description).trim();
  const sub = desc || (host ? host : "");

  return (
    <div className="cv-ad-banner" role="button" tabIndex={0} onClick={onClick} onKeyDown={onKeyDown}>
      <div className="cv-ad-badge">Реклама</div>

      <div className="cv-ad-media">
        {campaign.imageUrl ? (
          <img className="cv-ad-image" src={campaign.imageUrl} alt={title || "ad"} loading="lazy" />
        ) : (
          <div className="cv-ad-fallback">{title || "Реклама"}</div>
        )}
      </div>

      {/* Заголовок/опис під банером */}
      {(title || sub) ? (
        <div className="cv-ad-meta">
          {title ? <div className="cv-ad-title">{title}</div> : null}
          {sub ? <div className="cv-ad-sub">{sub}</div> : null}
        </div>
      ) : null}
    </div>
  );
}
